/**
 * UploadRubricModal - Modal for uploading rubrics from CSV
 */
import log from 'loglevel'
import React, { useState, useRef } from 'react'
import { Modal, Button, Spinner } from 'react-bootstrap'
import { FiInfo } from 'react-icons/fi'
import * as constants from 'Constants'
import httpService from 'services/httpService'
import displayGlobalMessage from 'Utils/DisplayGlobalMessage'

const UploadRubricModal = ({ show, onHide, activityId, questionId, isQuiz = false, onSuccess }) => {
  const [selectedFile, setSelectedFile] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef(null)

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      // Validate file type
      const fileName = file.name.toLowerCase()
      if (!fileName.endsWith('.csv')) {
        displayGlobalMessage('Only CSV files are allowed', 'error')
        return
      }
      setSelectedFile(file)
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) {
      displayGlobalMessage('Please select a file first', 'warning')
      return
    }

    setIsUploading(true)
    try {
      const targetId = questionId || activityId
      const isQuizMode = Boolean(isQuiz || questionId)
      const url = isQuizMode
        ? `${constants.base_url}/otherlabs/question/${targetId}/rubrics/upload/`
        : `${constants.base_url}/evalpro/rubric/upload_rubric/${targetId}`
      const formData = new FormData()
      formData.append('file', selectedFile)

      await httpService.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      displayGlobalMessage('Rubric uploaded successfully', 'success')
      setSelectedFile(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      onSuccess?.()
    } catch (error) {
      log.error('Error uploading rubric:', error)
      displayGlobalMessage('Failed to upload rubric. Please check the CSV format.', 'error')
    } finally {
      setIsUploading(false)
    }
  }

  const handleClose = () => {
    setSelectedFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    onHide()
  }

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          Import Rubrics
          <FiInfo size={18} className="text-muted" />
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="p-3 border rounded bg-light">
          <p className="text-muted mb-3">Accepted File Formats: .csv</p>

          <div className="d-flex align-items-center gap-2 mb-3">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
            >
              Choose File
            </Button>
            <span className="text-muted">
              {selectedFile ? selectedFile.name : 'No file chosen'}
            </span>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </div>

          <Button
            variant="primary"
            onClick={handleUpload}
            disabled={!selectedFile || isUploading}
          >
            {isUploading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Importing...
              </>
            ) : (
              'Import'
            )}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default UploadRubricModal
